import React, { memo } from 'react'

import {
  ArrowLeft, ArrowRight, Button, Icon,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  bool, func, number, oneOfType, shape, string,
} from 'prop-types'

import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import { imageShape } from 'Pods/ThreeDStudio/Shared/ImageModal/imageShape'
import References from 'Pods/ThreeDStudio/Shared/References/References'

import { NOOP } from 'Utils/stableDefaults'

import Actions from './Components/Actions/Actions'

const arrowIconSize = { height: '20px', width: '20px' }
// No `color` prop: Triangle's button rule forces the glyph to the button's `currentColor`
// with `!important`, so the arrows are coloured in _image_gallery_modal.scss instead.
const previousIcon = <Icon svgComponent={ArrowLeft} size={arrowIconSize} notSpaced />
const nextIcon = <Icon svgComponent={ArrowRight} size={arrowIconSize} notSpaced />

// Below 1280 the modal keeps its arrows on the stage, so the references run along the
// stage's bottom as the strip; from 1280 the arrows move outside and the column takes the
// left gutter.
const REFERENCES_STRIP_BREAKPOINTS = ['xs', 'sm', 'md']

function Preview({
  image,
  referencesRunId = null,
  hasPrevious = false,
  hasNext = false,
  onPrevious = NOOP,
  onNext = NOOP,
  isRegenerating = false,
  onRegenerate = null,
  onEdit = NOOP,
  onDownload = NOOP,
  onGenerate3d = NOOP,
}) {
  const t = useTranslation()

  return (
    <div className="image-gallery-modal__preview">
      <div className="image-gallery-modal__stage">
        <Button
          type="secondary"
          size="small"
          variant="only-icon"
          actionType="button"
          icon={previousIcon}
          customClass="image-gallery-modal__arrow image-gallery-modal__arrow--previous"
          disabled={!hasPrevious}
          onClick={onPrevious}
          aria-label={t('imageGallery.modal.previous')}
        />
        <img
          className="image-gallery-modal__image"
          src={image.src}
          alt={image.title}
          style={{ '--ratio': image.aspectRatio }}
        />
        <Button
          type="secondary"
          size="small"
          variant="only-icon"
          actionType="button"
          icon={nextIcon}
          customClass="image-gallery-modal__arrow image-gallery-modal__arrow--next"
          disabled={!hasNext}
          onClick={onNext}
          aria-label={t('imageGallery.modal.next')}
        />
        {/* Keyed on the image so stepping to the next one starts collapsed. */}
        <References
          key={image.id}
          runId={referencesRunId}
          customClass="image-gallery-modal__references"
          stripBreakpoints={REFERENCES_STRIP_BREAKPOINTS}
        />
      </div>
      <Actions
        title={image.title}
        slug={image.slug}
        likeCount={image.likeCount}
        isRegenerating={isRegenerating}
        onRegenerate={onRegenerate}
        onEdit={onEdit}
        onDownload={onDownload}
        onGenerate3d={onGenerate3d}
      />
    </div>
  )
}

Preview.propTypes = {
  image: shape(imageShape).isRequired,
  referencesRunId: oneOfType([string, number]),
  hasPrevious: bool,
  hasNext: bool,
  onPrevious: func,
  onNext: func,
  isRegenerating: bool,
  onRegenerate: func,
  onEdit: func,
  onDownload: func,
  onGenerate3d: func,
}

export default memo(Preview)
